/**
 * GoalServe's JSON is shaped per sport and, worse, per cardinality: a single
 * match arrives as an object where two arrive as an array, and the same field
 * is `localteam`/`visitorteam` in one sport and `hometeam`/`awayteam` in
 * another. Attribute values are prefixed with `@` (also when sports-api had to
 * parse an XML reply that ignored `?json=1`).
 *
 * Rather than a mapper per sport — which is how the single-sport APIs each
 * ended up with their own copy — these helpers walk the payload structurally
 * and pick up anything that looks like a match. Best effort by design: a
 * widget renders what it recognises and says so when it recognises nothing.
 */

export interface NormalizedMatch {
  id: string
  home: string
  away: string
  homeId?: string
  awayId?: string
  homeScore: string
  awayScore: string
  status: string
  time: string
  date?: string
  league?: string
  leagueId?: string
  /** Esports discipline/game from the feed's `@type` (e.g. "CS GO", "Dota",
   *  "League of Legends") — the league name is the tournament, so per-game
   *  filtering has to key off this instead. */
  discipline?: string
  /** Tennis: per-set games for each side (s1..s5) + who won — so the widget can
   *  render the Flashscore layout (sets won · 6-3 4-6 7-5) instead of one score. */
  homeSets?: string[]
  awaySets?: string[]
  homeWinner?: boolean
  awayWinner?: boolean
}

/** GoalServe collapses single-element lists into the element itself. */
export function toArray<T>(value: T | T[] | undefined | null): T[] {
  if (value === undefined || value === null) return []
  return Array.isArray(value) ? value : [value]
}

/** Read a field that may be a bare value, an `@`-prefixed attribute, or absent. */
export function text(node: unknown, ...keys: string[]): string {
  if (!isRecord(node)) return ''
  for (const key of keys) {
    const value = node[key] ?? node[`@${key}`]
    if (typeof value === 'string' || typeof value === 'number') return String(value)
    if (isRecord(value)) {
      const nested = value['@name'] ?? value['name'] ?? value['@value'] ?? value['#text']
      if (typeof nested === 'string' || typeof nested === 'number') return String(nested)
    }
  }
  return ''
}

const HOME_KEYS = ['localteam', 'hometeam', 'home_team', 'player_1']
const AWAY_KEYS = ['visitorteam', 'awayteam', 'away_team', 'player_2']

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function teamOf(node: Record<string, unknown>, keys: string[]): unknown {
  for (const key of keys) {
    const value = node[key] ?? node[`@${key}`]
    if (value !== undefined) return value
  }
  return undefined
}

/**
 * Individual sports (tennis, and other player-vs-player feeds) put both sides in
 * a `player` array of two rather than home/away fields. Returns the pair when
 * the node is shaped that way, so a match with players reads like any other.
 */
function playerPair(node: Record<string, unknown>): [unknown, unknown] | null {
  const players = node.player ?? node['@player']
  if (Array.isArray(players) && players.length >= 2) return [players[0], players[1]]
  return null
}

function looksLikeMatch(node: Record<string, unknown>): boolean {
  if (teamOf(node, HOME_KEYS) !== undefined && teamOf(node, AWAY_KEYS) !== undefined) return true
  return playerPair(node) !== null
}

/**
 * Depth-first walk collecting every match-shaped node, carrying the nearest
 * enclosing league/category name down with it. `limit` stops the walk early —
 * some feeds carry thousands of fixtures and a widget shows a dozen.
 */
export function collectMatches(payload: unknown, limit = 50): NormalizedMatch[] {
  const out: NormalizedMatch[] = []

  const walk = (node: unknown, league: string, leagueId: string, path: string): void => {
    if (out.length >= limit) return

    if (Array.isArray(node)) {
      node.forEach((child, index) => walk(child, league, leagueId, `${path}.${index}`))
      return
    }
    if (!isRecord(node)) return

    const ownName = text(node, 'name') || text(node, 'league')
    const nextLeague = ownName || league
    // The nearest named container's id is the league/tournament id.
    const nextLeagueId = ownName ? text(node, 'id') || leagueId : leagueId

    if (looksLikeMatch(node)) {
      const pair = playerPair(node)
      const home = pair ? pair[0] : teamOf(node, HOME_KEYS)
      const away = pair ? pair[1] : teamOf(node, AWAY_KEYS)
      // Set-based sports carry per-set scores as s1..s5 on each side: tennis
      // players list games per set (with an explicit @winner), volleyball teams
      // list points per set (no winner flag — sets won is `totalscore`). Collect
      // the non-empty ones so the widget can render the Flashscore set layout.
      const setsOf = (side: unknown): string[] =>
        ['s1', 's2', 's3', 's4', 's5'].map((k) => text(side, k)).filter((v) => v !== '')
      const homeSets = setsOf(home)
      const awaySets = setsOf(away)
      const hasSets = homeSets.length > 0
      const homeScore = text(home, 'totalscore', 'score', 'goals')
      const awayScore = text(away, 'totalscore', 'score', 'goals')
      // Winner: tennis exposes @winner directly; team set-sports (volleyball)
      // have none, so infer it from who took more sets (higher totalscore).
      const homeTotal = Number(homeScore)
      const awayTotal = Number(awayScore)
      const totalsComparable =
        hasSets && Number.isFinite(homeTotal) && Number.isFinite(awayTotal) && homeTotal !== awayTotal
      const homeWinner = pair
        ? text(home, 'winner') === 'True'
        : totalsComparable
          ? homeTotal > awayTotal
          : undefined
      const awayWinner = pair
        ? text(away, 'winner') === 'True'
        : totalsComparable
          ? awayTotal > homeTotal
          : undefined
      out.push({
        id: text(node, 'id', 'fix_id', 'static_id') || path,
        home: text(home, 'name') || String(home ?? ''),
        away: text(away, 'name') || String(away ?? ''),
        homeId: text(home, 'id') || undefined,
        awayId: text(away, 'id') || undefined,
        homeScore,
        awayScore,
        status: text(node, 'status', 'state'),
        time: text(node, 'time'),
        date: text(node, 'date', 'formatted_date') || undefined,
        league: nextLeague || undefined,
        leagueId: nextLeagueId || undefined,
        discipline: text(node, 'type') || undefined,
        homeSets: hasSets ? homeSets : undefined,
        awaySets: hasSets ? awaySets : undefined,
        homeWinner,
        awayWinner,
      })
      return
    }

    for (const [key, value] of Object.entries(node)) {
      if (key.startsWith('@')) continue
      walk(value, nextLeague, nextLeagueId, `${path}.${key}`)
    }
  }

  walk(payload, '', '', 'root')
  return out
}

export interface NormalizedStandingRow {
  id: string
  position: string
  team: string
  played: string
  won: string
  drawn: string
  lost: string
  points: string
  group?: string
}

const STANDING_SIGNALS = ['position', 'rank', 'won', 'win', 'lost', 'points', 'pts']

function looksLikeStandingRow(node: Record<string, unknown>): boolean {
  if (!text(node, 'name', 'team')) return false
  return STANDING_SIGNALS.some(
    (key) => node[key] !== undefined || node[`@${key}`] !== undefined,
  )
}

/**
 * Same structural walk as {@link collectMatches}, for league tables. Standings
 * feeds nest a group/conference name above the rows in some sports and not in
 * others, so the nearest enclosing name is carried down and surfaced as `group`
 * for the widget to decide whether to show.
 */
export function collectStandings(payload: unknown, limit = 100): NormalizedStandingRow[] {
  const out: NormalizedStandingRow[] = []

  const walk = (node: unknown, group: string, path: string): void => {
    if (out.length >= limit) return

    if (Array.isArray(node)) {
      node.forEach((child, index) => walk(child, group, `${path}.${index}`))
      return
    }
    if (typeof node !== 'object' || node === null) return

    const record = node as Record<string, unknown>
    const ownName = text(record, 'league') || text(record, 'group')
    const nextGroup = ownName || group

    if (looksLikeStandingRow(record)) {
      out.push({
        id: text(record, 'id') || path,
        position: text(record, 'position', 'rank', 'pos'),
        team: text(record, 'name', 'team'),
        played: text(record, 'games_played', 'played', 'overall_gp', 'gp', 'p'),
        won: text(record, 'won', 'win', 'w'),
        drawn: text(record, 'drawn', 'draw', 'draws', 'ties', 'tie', 'd'),
        lost: text(record, 'lost', 'loss', 'l'),
        points: text(record, 'points', 'pts'),
        group: nextGroup || undefined,
      })
      return
    }

    for (const [key, value] of Object.entries(record)) {
      if (key.startsWith('@')) continue
      walk(value, nextGroup, `${path}.${key}`)
    }
  }

  walk(payload, '', 'root')
  return out
}

/**
 * The league/competition display name from a standings feed, so a table can
 * title itself instead of showing a raw id. GoalServe nests it as
 * `standings.category[].league.name` (sometimes `.name`), and sometimes leaves
 * it blank — returns '' then, for the caller to fall back to a heading.
 */
export function standingsLeagueName(payload: unknown): string {
  let found = ''
  const visit = (node: unknown): void => {
    if (found || !node || typeof node !== 'object') return
    if (Array.isArray(node)) {
      node.forEach(visit)
      return
    }
    const record = node as Record<string, unknown>
    // A category's own league name, before we descend into the team rows.
    if (!looksLikeStandingRow(record)) {
      const league = record.league ?? record['@league']
      const name =
        (league && typeof league === 'object' ? text(league as Record<string, unknown>, 'name') : '') ||
        (typeof league === 'string' ? league : '') ||
        text(record, 'league_name', 'competition', 'tournament')
      if (name) {
        found = name
        return
      }
    }
    for (const [key, value] of Object.entries(record)) {
      if (key.startsWith('@')) continue
      visit(value)
    }
  }
  visit(payload)
  return found
}
